import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AppConfig, appConfigService } from '@app/config';
import { AuthService } from '@app/components/login.component/service/auth.service';
import { AuthRequestOptions } from '@ntap/core';
import { NTAPMenuItem, NTAPMenuService } from '@ntap/common';
import { map, Observable, switchMap } from 'rxjs';
import {
  LandingOption,
  UiPreferences,
  UiPreferencesUpdate
} from './ui-preferences.model';

/**
 * GAP-UC-43 — Real data-access service for per-user UI preferences.
 *
 * HTTP CONVENTION (verified in source/earchive-ui — followed, not re-invented):
 *  - Constructor-injected `HttpClient` (existing services use constructor DI, e.g.
 *    auth.service.ts, change-password.service.ts). `httpResource()` / functional
 *    interceptors are NOT used anywhere in this app (interceptors are DI-based via
 *    `provideHttpClient(withInterceptorsFromDi())`), so we do not introduce them
 *    (governance + architecture-tech: follow the established pattern).
 *  - URLs are built from the runtime-injected config: `config.ntapEnvironment.pathApiNtap`
 *    (= `${apiHost}api/ntap`), exactly as auth.service.ts builds `.../ui/me`.
 *  - CSRF for the mutating PUT: fetch a token via `AuthService.getCsrfToken()` then send
 *    with `new AuthRequestOptions()` (withCredentials + NTAP CSRF header handling) — the
 *    same mechanism used by change-password.service.ts and auth.service.ts.
 *  - Per-user scoping is server-side from the session; no userId is ever sent
 *    (api-contract §3; REQ-GAPUC43-019).
 *
 * Contract: api-contract.md §4/§5, openapi.yaml (getMyUiPreferences / saveMyUiPreferences).
 */
@Injectable({ providedIn: 'root' })
export class UiPreferencesService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private menuService: NTAPMenuService,
    @Inject(appConfigService) private config: AppConfig
  ) {}

  private preferencesUrl(): string {
    // pathApiNtap === `${apiHost}api/ntap` (environment.ts). Endpoint: api/ntap/ui/preferences.
    return (this.config.ntapEnvironment?.pathApiNtap ?? '') + '/ui/preferences';
  }

  /**
   * GET api/ntap/ui/preferences — read the acting user's preferences (REQ-GAPUC43-012).
   * 200 with null fields when no record saved (api-contract §5.1). Session-scoped read
   * needs no CSRF; `AuthRequestOptions` carries `withCredentials`.
   */
  load(): Observable<UiPreferences> {
    return this.http.get<UiPreferences>(
      this.preferencesUrl(),
      new AuthRequestOptions()
    );
  }

  /**
   * PUT api/ntap/ui/preferences — upsert the acting user's preferences (REQ-GAPUC43-011).
   * Fetches a fresh CSRF token first (state-changing call), then PUTs with
   * `AuthRequestOptions` — identical to the app's change-password flow.
   */
  save(body: UiPreferencesUpdate): Observable<UiPreferences> {
    return this.authService
      .getCsrfToken()
      .pipe(
        switchMap(() =>
          this.http.put<UiPreferences>(
            this.preferencesUrl(),
            body,
            new AuthRequestOptions()
          )
        )
      );
  }

  /**
   * Landing-dropdown OPTIONS sourced from the REAL role-filtered menu
   * (`getAllowedMenus`, exposed here via the app's `NTAPMenuService` = `ArhivMenuService`).
   * Filters to navigable nodes only: `routerLink != null`, excluding pure containers and
   * external `url`-only items; displays `label`, stores `name` (REQ-GAPUC43-006/007/008).
   */
  landingOptions(): Observable<LandingOption[]> {
    return this.menuService
      .getMenuItems()
      .pipe(
        map((forest) => UiPreferencesService.toLandingOptions(forest ?? []))
      );
  }

  /** Flatten the role-filtered menu forest into navigable landing options (REQ-GAPUC43-008). */
  static toLandingOptions(forest: NTAPMenuItem[]): LandingOption[] {
    const out: LandingOption[] = [];
    const seen = new Set<string>();
    const walk = (nodes: NTAPMenuItem[]): void => {
      for (const node of nodes) {
        const name = UiPreferencesService.menuName(node);
        // Navigable iff it has a routerLink AND a stable name. External url-only items have
        // no routerLink; pure containers have items but no routerLink -> both excluded.
        if (node.routerLink != null && name && !seen.has(name)) {
          seen.add(name);
          out.push({ label: node.label ?? name, value: name });
        }
        if (node.items?.length) {
          walk(node.items);
        }
      }
    };
    walk(forest);
    return out;
  }

  /**
   * Re-resolve a stored landing `name` to its current `routerLink` using a FRESH
   * role-filtered menu (REQ-GAPUC43-013). Returns null when the name is no longer
   * navigable/visible for the user (feeds the OQ-CI-2 stale-landing fallback).
   */
  resolveLanding(name: string | null): Observable<string | null> {
    return this.menuService
      .getMenuItems()
      .pipe(
        map((forest) => UiPreferencesService.findRouterLink(forest ?? [], name))
      );
  }

  /** Depth-first lookup of a navigable node's normalized routerLink by menu `name`. */
  static findRouterLink(
    forest: NTAPMenuItem[],
    name: string | null
  ): string | null {
    if (!name) {
      return null;
    }
    for (const node of forest) {
      if (
        UiPreferencesService.menuName(node) === name &&
        node.routerLink != null
      ) {
        return UiPreferencesService.normalizeRouterLink(node.routerLink);
      }
      if (node.items?.length) {
        const found = UiPreferencesService.findRouterLink(node.items, name);
        if (found != null) {
          return found;
        }
      }
    }
    return null;
  }

  /**
   * `name` is the stable persistable key (api-contract §5.3). It is present on the runtime
   * NTAP menu payload but is not declared on the published `NTAPMenuItem` type, so it is
   * read defensively (existing code accesses `menuitem.name` the same way).
   */
  private static menuName(node: NTAPMenuItem): string | null {
    const value = (node as unknown as { name?: unknown }).name;
    return typeof value === 'string' && value.length > 0 ? value : null;
  }

  /** routerLink may be a string or a segment array (e.g. ['/moji-zadaci']); normalize to a path. */
  private static normalizeRouterLink(routerLink: unknown): string {
    if (Array.isArray(routerLink)) {
      return routerLink
        .map((s) => `${s}`)
        .join('/')
        .replace(/\/{2,}/g, '/');
    }
    return `${routerLink}`;
  }
}
